import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, Subject, throwError, timer } from 'rxjs';
import { catchError, finalize, groupBy, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AtoaWebSdk, EnvironmentTypeEnum } from '@atoapayments/atoa-web-client-sdk';
import { ConfigureService } from '@app/configure/services/configure.service';
import { ConfigureAPIService, InitPaymentPayload } from '@app/configure/services/configure-api.service';
import { PaymentType, Provider } from '../components/payment/payment.component';
import { environment } from '@environments/environment';

export type PaymentProcessResponse = {
    qrCodeUrl: string;
    qrCodeUrlPng: string;
    paymentUrl: string;
    paymentRequestId: string;
    paymentServiceId: string;
    expiresIn: number;
};

export const PaymentStatus = {
    IDLE: 'IDLE',
    COMPLETED: 'COMPLETED',
    PENDING: 'PENDING',
    PENDING_FAILED: 'PENDING_FAILED',
    FAILED: 'FAILED',
    PAYMENT_NOT_INITIATED: 'PAYMENT_NOT_INITIATED',
} as const;

export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

type ActionType = 'init' | 'poll' | 'refresh' | 'globalTimeout';

interface Action {
    type: ActionType;
    delayMs: number;
    cancel?: boolean;
}

const POLLING_INTERVAL_MS = 3000;
const GLOBAL_PENDING_TIMEOUT_MS = 180000;
const LINKS_EXPIRE_IN_SECONDS = 90;
const PHONE_COUNTRY_CODE = '44';
const PAYMENT_PROVIDER = 'atoa';

const TERMINAL_STATUSES = new Set<PaymentStatus>([
    PaymentStatus.PENDING_FAILED,
    PaymentStatus.COMPLETED,
    PaymentStatus.FAILED,
]);

/**
 * Manages the full lifecycle of an AtoA open banking payment, including
 * SDK initialisation, link refresh, payment status polling, and teardown.
 *
 * All asynchronous work (HTTP requests, timers, polling) is driven through
 * a single unified RxJS action stream. Each action type (`init`, `poll`,
 * `refresh`, `globalTimeout`) runs in its own `switchMap` lane via `groupBy`,
 * so emitting a new action of the same type automatically cancels the previous one.
 *
 * @example
 * // Provided at the component level, not root — each payment component
 * // gets its own isolated instance.
 * @Component({
 *   providers: [AtoaPaymentService],
 * })
 */
@Injectable()
export class AtoaPaymentService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private action$ = new Subject<Action>();

    private isGlobalPendingTimerActive = false;
    private _sdkLoading = true;
    private _paymentProcessResponse: PaymentProcessResponse | undefined;
    private _paymentInitSuccess = false;
    private sdk: AtoaWebSdk | undefined;
    private isConfigSyncInitialised = false;
    private isConfigRefreshInFlight = false;
    private isAwaitingConfigForInit = false;

    private paymentStatus = new BehaviorSubject<PaymentStatus>(PaymentStatus.IDLE);

    /** Observable stream of the current payment status for use in templates and consumers. */
    paymentStatus$: Observable<PaymentStatus> = this.paymentStatus.asObservable();

    /**
     * Whether the SDK is currently being fetched and initialised.
     * `true` from the moment {@link setSDK} is called until the SDK
     * has been fully constructed, or after {@link disposeAtoaSDK}.
     */
    get sdkLoading(): boolean {
        return this._sdkLoading;
    }

    /**
     * The latest payment process response from the API.
     * Contains QR code URLs, payment request IDs, and expiry information
     * needed by the template to render the payment UI.
     *
     * Returns `undefined` before the first successful SDK initialisation
     * and after disposal.
     */
    get paymentProcessResponse(): PaymentProcessResponse | undefined {
        return this._paymentProcessResponse;
    }

    /**
     * Whether the payment initialisation request succeeded.
     * `true` if the API request was successful, `false` otherwise.
     */
    get paymentInitSuccess(): boolean {
        return this._paymentInitSuccess;
    }

    private provider: Provider;
    private enquiryHash: string;
    private enquiryUlid: string;
    private customer: { ulid?: string; tel_mob?: string; email?: string };
    private reservationAmount: number;
    private paymentType: PaymentType = 'reserve';

    constructor(
        private configureService: ConfigureService,
        private configureApiService: ConfigureAPIService,
    ) {
        const data = this.configureService.configure_data;

        this.enquiryHash = data.meta.enquiry_hash;
        this.enquiryUlid = data.meta.enquiry_ulid;
        this.customer = data.meta.customer;
        this.reservationAmount = data.steps.payment.data.reservation.reservation_amount;

        this.initActionStream();
        this.initConfigSync();
    }

    ngOnDestroy(): void {
        this.cancelActiveTimers();
        this.destroy$.next();
        this.destroy$.complete();
        this.disposeAtoaSDK();
    }

    /**
     * Store the selected payment provider configuration.
     * Must be called before {@link setSDK}.
     *
     * @param provider - The provider selected by the user (contains `settingId`, bank logos, etc.)
     */
    setProviderInfo(provider: Provider): void {
        this.provider = provider;
    }

    /**
     * Set whether this is a reservation or full buy-now payment.
     * Must be called before {@link setSDK}.
     *
     * @param paymentType - `'reserve'` or `'buyNow'`
     */
    setPaymentType(paymentType: PaymentType): void {
        this.paymentType = paymentType;
    }

    /**
     * Trigger a request to the API to fetch SDK options,
     * then initialise the AtoA SDK.
     *
     * Safe to call multiple times — previous in-flight requests
     * are automatically cancelled via `switchMap`.
     */
    setSDK(): void {
        this.dispatch('init');
    }

    /**
     * Open the AtoA payment dialog for the current SDK instance.
     * Automatically sets payment status to `PENDING` and begins polling.
     * If the SDK throws, triggers a hard refresh.
     */
    openPaymentDialog(): void {
        this.triggerPendingLogic();
        try {
            this.sdk.showPaymentDialog();
        } catch (error) {
            this.refreshSDK(true);
        }
    }

    /**
     * Dispose the SDK instance, clear the payment response,
     * and reset status to `IDLE`.
     *
     * Safe to call multiple times — guards against double-disposal.
     */
    disposeAtoaSDK(): void {
        this.cancelActiveTimers();
        this._paymentProcessResponse = undefined;
        this._sdkLoading = true;
        this.paymentStatus.next(PaymentStatus.IDLE);

        if (this.sdk) {
            this.sdk.dispose();
            this.sdk = undefined;
        }
    }

    /**
     * Initialise the single unified action stream.
     *
     * Uses `groupBy` to split actions into independent lanes by type,
     * then `switchMap` within each lane so that a new emission of the
     * same type cancels the previous timer/request. This replaces the
     * need for separate subjects and manual timer teardown.
     */
    private initActionStream(): void {
        this.action$
            .pipe(
                groupBy(action => action.type),
                mergeMap(group$ => {
                    switch (group$.key) {
                        case 'init':
                            return group$.pipe(
                                switchMap(action => {
                                    if (action.cancel) return EMPTY;
                                    return timer(action.delayMs).pipe(
                                        switchMap(() => this.initPaymentRequest$()),
                                    );
                                }),
                            );
                        case 'refresh':
                            return group$.pipe(
                                switchMap(action => {
                                    if (action.cancel) return EMPTY;
                                    return timer(action.delayMs).pipe(
                                        tap(() => this.refreshSDK()),
                                    );
                                }),
                            );
                        case 'poll':
                            return group$.pipe(
                                switchMap(action => {
                                    if (action.cancel) return EMPTY;
                                    return timer(action.delayMs).pipe(
                                        switchMap(() => this.pollPaymentStatus$()),
                                    );
                                }),
                            );
                        case 'globalTimeout':
                            return group$.pipe(
                                switchMap(action => {
                                    if (action.cancel) return EMPTY;
                                    this.isGlobalPendingTimerActive = true;
                                    return timer(action.delayMs).pipe(
                                        tap(() => this.setPaymentPendingFailed()),
                                        finalize(() => (this.isGlobalPendingTimerActive = false)),
                                    );
                                }),
                            );
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Emit an action into the unified stream.
     *
     * @param type - The action lane to target
     * @param delayMs - Delay in milliseconds before the action executes (default `0`)
     */
    private dispatch(type: ActionType, delayMs: number = 0, cancel = false): void {
        this.action$.next({ type, delayMs, cancel });
    }

    /** Cancel all currently active action timers (poll, refresh, init delays, and global timeout). */
    private cancelActiveTimers(): void {
        this.dispatch('poll', 0, true);
        this.dispatch('refresh', 0, true);
        this.dispatch('init', 0, true);
        this.dispatch('globalTimeout', 0, true);
        this.isGlobalPendingTimerActive = false; // Reset your global state
    }

 

    /**
     * Subscribe to global configure data changes and keep
     * local references in sync for the lifetime of the service.
     * 
     * If `enquiryUlid` or `customer.ulid` are missing, triggers a fresh
     * fetch from the API to retrieve updated data.
     */
    private initConfigSync(): void {
        if (this.isConfigSyncInitialised) {
            this.refreshConfigureDataIfRequired();
            return;
        }

        this.isConfigSyncInitialised = true;

        this.configureService.configure_data$
            .pipe(takeUntil(this.destroy$))
            .subscribe(configureData => {
                this.enquiryHash = configureData.meta.enquiry_hash;
                this.enquiryUlid = configureData.meta.enquiry_ulid;
                this.customer = configureData.meta.customer;

                this.refreshConfigureDataIfRequired();

                if (this.isAwaitingConfigForInit && this.hasRequiredPaymentConfig()) {
                    this.isAwaitingConfigForInit = false;
                    this.dispatch('init');
                }
            });
    }

    /** Whether the minimum enquiry/customer identifiers required for payment init are present. */
    private hasRequiredPaymentConfig(): boolean {
        return Boolean(this.enquiryUlid && this.customer?.ulid);
    }

    /** Check if configure data has the required IDs for payment processing. */
    private hasRequiredIds(data: any): boolean {
        return Boolean(data?.meta?.enquiry_ulid && data?.meta?.customer?.ulid);
    }

    /** Set the failure state for payment initialization. */
    private setPaymentInitFailure(): void {
        this._paymentInitSuccess = false;
        this.disposeAtoaSDK();
        this._sdkLoading = false;
    }

    /** Refetch configure data when payment-critical identifiers are missing. */
    private refreshConfigureDataIfRequired(): void {
        if (this.hasRequiredPaymentConfig() || this.isConfigRefreshInFlight || !this.enquiryHash) {
            return;
        }

        this.isConfigRefreshInFlight = true;

        this.configureApiService
            .getConfigureData(this.enquiryHash)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isConfigRefreshInFlight = false)),
                catchError(() => EMPTY),
            )
            .subscribe(response => {
                if (response.status === 200) {
                    const fetchedData = response.body;
                    
                    if (this.hasRequiredIds(fetchedData)) {
                        this.configureService.setData(fetchedData, true);
                    } else {
                        // Data still missing required IDs after refresh — enter failure state
                        this.setPaymentInitFailure();
                    }
                }
            });
    }

    /**
     * Build the payment payload and call the API to initialise payment.
     * On success, stores the response, schedules a link refresh and
     * poll cycle, then boots the SDK.
     *
     * @returns An observable that completes after the API response is handled
     */
    private initPaymentRequest$(): Observable<void> {
        this._sdkLoading = true;

        if (!this.hasRequiredPaymentConfig()) {
            // Retrigger config sync/refresh and retry init as soon as data is available.
            this.isAwaitingConfigForInit = true;
            this.initConfigSync();
            return EMPTY;
        }

        const payload: InitPaymentPayload = {
            amount: this.reservationAmount,
            consumerId: this.customer.ulid,
            callbackParams: {
                paymentType: this.paymentType === 'reserve' ? 'reservation' : this.paymentType,
            },
            initiationParameters: {
                expiresIn: LINKS_EXPIRE_IN_SECONDS,
            },
            enquiryId: this.enquiryUlid,
            settingId: this.provider.settingId,
        };

        return this.configureApiService.initPayment(this.enquiryHash, PAYMENT_PROVIDER, payload).pipe(
            switchMap(response => {
                if (!response.body?.success) {
                   return throwError(() => new Error('Payment initialization failed'));
                }
                return of(response);
            }),
            tap(response => {
                this._paymentInitSuccess = true;
                this._paymentProcessResponse = response.body;
                const expiresInMs = this._paymentProcessResponse.expiresIn * 1000;
                this.dispatch('refresh', expiresInMs);
                this.dispatch('poll', POLLING_INTERVAL_MS);
                this.initialiseAtoaSDK();
            }),
            catchError(err => {
                this.setPaymentInitFailure();
                return EMPTY;
            }),
            finalize(() => (this._sdkLoading = false)),
        );
    }

    /**
     * Poll the API for the current payment status.
     * Re-schedules itself on empty responses or errors to
     * keep polling until a terminal status is reached.
     *
     * @returns An observable that completes after the API response is handled,
     *          or `EMPTY` if there is no active payment response to poll against
     */
    private pollPaymentStatus$(): Observable<void> {
        if (!this._paymentProcessResponse) {
            return EMPTY;
        }

        return this.configureApiService
            .getPaymentStatus(this.enquiryHash, PAYMENT_PROVIDER, this._paymentProcessResponse.paymentServiceId)
            .pipe(
                switchMap(response => {
                    if (response.body.length === 0) {
                        this.dispatch('poll', POLLING_INTERVAL_MS);
                    } else {
                        this.handleStatus(response.body.data.status);
                    }
                    return EMPTY;
                }),
                catchError(() => {
                    if (this._paymentProcessResponse) {
                        this.dispatch('poll', POLLING_INTERVAL_MS);
                    }
                    return EMPTY;
                }),
            );
    }

    /**
     * Initialise the AtoA Web SDK with the current payment response.
     * If no response exists yet, falls back to {@link setSDK} to fetch one first.
     *
     * Disposes any existing SDK instance before creating a new one.
     * Sets {@link sdkLoading} to `false` once the SDK is ready.
     *
     * @see https://docs.atoa.me/web-client-sdk
     */
    private initialiseAtoaSDK(): void {
        if (!this._paymentProcessResponse) {
            this.setSDK();
            return;
        }

        if (this.sdk) {
            this.sdk.dispose();
        }

        this.sdk = new AtoaWebSdk({
            environment: environment.atoa_use_sandbox
                ? EnvironmentTypeEnum.SANDBOX
                : EnvironmentTypeEnum.PRODUCTION,
            paymentRequestId: this._paymentProcessResponse.paymentRequestId,
            customerDetails: {
                phoneCountryCode: PHONE_COUNTRY_CODE,
                phoneNumber: this.customer.tel_mob,
                email: this.customer.email,
            },
            onUserCancel: () => {
                this.refreshSDK(true);
            },
            onPaymentStatusChange: data => {
                this.handleStatus(data.status);
            },
            onError: () => {
                this.refreshSDK(true);
            },
        });

        this._sdkLoading = false;
    }

    /**
     * Dispose the current SDK and re-initialise from scratch.
     * Only acts when the current status is `IDLE`, unless `hardRefresh` is `true`.
     *
     * @param hardRefresh - When `true`, forces a refresh regardless of current status
     */
    private refreshSDK(hardRefresh = false): void {
        if (this.paymentStatus.getValue() === PaymentStatus.IDLE || hardRefresh) {
            this.disposeAtoaSDK();
            this.setSDK();
        }
    }

    /** Whether the payment status can still be updated (i.e. has not reached a terminal state). */
    private get canUpdateStatus(): boolean {
        return !TERMINAL_STATUSES.has(this.paymentStatus.getValue());
    }

    /**
     * Route a status string from the SDK or API into the appropriate action.
     *
     * - `PAYMENT_NOT_INITIATED` → re-poll
     * - `COMPLETED` → mark completed
     * - `FAILED` → mark failed
     * - Anything else while `PENDING` → re-poll (waiting for a decisive status)
     * - Anything else → enter pending logic
     *
     * No-ops if the current status is already terminal.
     *
     * @param status - The raw status string from the SDK callback or API response
     */
    private handleStatus(status: string): void {
        if (!this.canUpdateStatus) {
            return;
        }

        if (
            this.paymentStatus.getValue() === PaymentStatus.PENDING &&
            status !== PaymentStatus.COMPLETED &&
            status !== PaymentStatus.FAILED
        ) {
            this.dispatch('poll', POLLING_INTERVAL_MS);
            return;
        }

        switch (status) {
            case PaymentStatus.PAYMENT_NOT_INITIATED:
                this.dispatch('poll', POLLING_INTERVAL_MS);
                break;
            case PaymentStatus.COMPLETED:
                this.cancelActiveTimers();
                this.paymentStatus.next(PaymentStatus.COMPLETED);
                break;
            case PaymentStatus.FAILED:
                this.cancelActiveTimers();
                this.paymentStatus.next(PaymentStatus.FAILED);
                break;
            default:
                this.triggerPendingLogic();
                break;
        }
    }

    /**
     * Transition to `PENDING_FAILED` — called when the global timeout
     * fires without the payment reaching a decisive status.
     */
    private setPaymentPendingFailed(): void {
        this.paymentStatus.next(PaymentStatus.PENDING_FAILED);
    }

    /**
     * Enter the pending state and begin polling.
     *
     * Starts the global pending timeout (once) which will transition
     * to `PENDING_FAILED` if no decisive status arrives in time,
     * and schedules a poll cycle to keep checking for updates.
     */
    private triggerPendingLogic(): void {
        if (!this.isGlobalPendingTimerActive) {
            this.dispatch('globalTimeout', GLOBAL_PENDING_TIMEOUT_MS);
            this.paymentStatus.next(PaymentStatus.PENDING);
        }

        this.dispatch('poll', POLLING_INTERVAL_MS);
    }

    get reversationAmount(): number {
        return this.reservationAmount;
    }
}
 